import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';

import { TranslateService } from '@ngx-translate/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ContactType } from '../../core/enums/contact-type.enum';

import { Contact } from '../../core/models/contact.model';

import { AccountService } from '../../core/services/account.service';
import { AnalyticsService } from '../../core/services/analytics.service';
import { ContactService } from '../../core/services/contact.service';
import { LoggerService } from '../../core/services/logger.service';
import { ModalService } from '../../core/services/modal.service';
import { OverlayService } from '../../core/services/overlay.service';
import { SettingsService } from '../../core/services/settings.service';
import { ValidationService } from '../../core/services/validation.service';

import { AnalyticsConfig, TOKEN_ANALYTICS_CONFIG } from '../../core/config/analytics.config';
import { AppConfig, TOKEN_CONFIG } from '../../core/config/app.config';

export const ContactModalPageIdentifier = 'contact-modal';

@Component({
  selector: 'app-contact-modal',
  templateUrl: './contact-modal.page.html',
  styleUrls: ['./contact-modal.page.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ContactModalPage implements OnInit, OnDestroy {
  @Input()
  public contactType: ContactType;

  public contactForm: FormGroup<{
    name: FormControl<string>;
    email: FormControl<string>;
    phoneNumber: FormControl<string>;
    comments: FormControl<string>;
  }>;
  public submitAttempt: boolean;
  public optionalText: string;

  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private formBuilder: NonNullableFormBuilder,
    private overlayService: OverlayService,
    private validationService: ValidationService,
    private contactService: ContactService,
    private loggerService: LoggerService,
    private translateService: TranslateService,
    private analyticsService: AnalyticsService,
    private modalService: ModalService,
    private settingsService: SettingsService,
    private accountService: AccountService,
    @Inject(TOKEN_CONFIG) private config: AppConfig,
    @Inject(TOKEN_ANALYTICS_CONFIG) private analyticsConfig: AnalyticsConfig,
  ) {}

  ngOnInit() {
    this.optionalText = this.contactType === ContactType.Feedback ? `(${this.translateService.instant('FIELDS.OPTIONAL')})` : '';
    this.initializeForm();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  public onDismiss(): void {
    void this.modalService.dismissModal({ id: ContactModalPageIdentifier });
  }

  public onSubmit(formElement: HTMLElement): void {
    this.submitAttempt = true;

    if (this.contactForm.valid) {
      void this.overlayService.showLoading().then(() => {
        const model = this.getModelFromForm();

        this.trackSubmitEvent(model);

        this.contactService
          .submit(model)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe({
            next: () => {
              void this.overlayService.hideLoading().then(() =>
                this.modalService.showAlert({
                  title: this.translateService.instant('SUCCESS') as string,
                  message: this.translateService.instant('CONTACT_PAGE.SUCCESS_MESSAGE') as string,
                  buttons: [
                    {
                      text: this.translateService.instant('OK') as string,
                      handler: () => this.onDismiss(),
                    },
                  ],
                }),
              );
            },
            error: (error: HttpErrorResponse) => {
              const errorMessage = this.validationService.getErrorMessage(error);

              this.loggerService.info({
                component: 'ContactPage',
                message: "couldn't submit contact form",
                details: { error, messageShownToUser: errorMessage },
              });

              void this.overlayService.hideLoading().then(() =>
                this.modalService.showAlert({
                  title: this.translateService.instant('ERROR') as string,
                  message: errorMessage,
                }),
              );
            },
          });
      });
    } else {
      this.validationService.focusFirstInvalidField(formElement);
    }
  }

  private initializeForm(): void {
    const user = this.accountService.userDetails;

    this.submitAttempt = false;

    this.contactForm = this.formBuilder.group({
      name: [
        user?.firstName ?? '',
        this.contactType === ContactType.Feedback ? null : [Validators.required, Validators.maxLength(this.config.nameMaxLength)],
      ],
      phoneNumber: [
        `${user?.phoneNumber ?? this.settingsService.getPhoneNumberCountryCode()}`,
        this.contactType === ContactType.Feedback
          ? null
          : [Validators.required, Validators.pattern(this.settingsService.getCountry().phoneNumberValidationPattern)],
      ],
      email: [
        user?.email ?? '',
        this.contactType === ContactType.Feedback ? null : [Validators.required, this.validationService.emailValidator],
      ],
      comments: ['', [Validators.required]],
    });
  }

  private getModelFromForm(): Contact {
    const formValue = this.contactForm.getRawValue();

    return {
      contactType: this.contactType,
      name: formValue.name || 'Unknown',
      phone: formValue.phoneNumber || null,
      email: formValue.email || 'unknown@bilbayt.com',
      comments: formValue.comments,
    };
  }

  private trackSubmitEvent(contactDetails: Contact): void {
    void this.analyticsService.trackEvent({
      name: this.analyticsConfig.eventName.contactPageSubmit,
      data: {
        [this.analyticsConfig.propertyName.name]: contactDetails.name,
        [this.analyticsConfig.propertyName.mobile]: contactDetails.phone,
        [this.analyticsConfig.propertyName.email]: contactDetails.email,
        [this.analyticsConfig.propertyName.comments]: contactDetails.comments,
      },
    });
  }
}
